import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { analyticsAPI } from '../../services/api';
import {
  ArrowLeft, User, BookOpen, Clock, Award, CheckCircle, Circle, ChevronDown,
  ChevronRight, Layers, FileText, Activity, Play, BarChart2,
} from 'lucide-react';
import PersonalModuleSummaryModal from '../../components/common/PersonalModuleSummaryModal';
import './Analytics.css';
import './TraineeAnalytics.css';

/* Radial progress ring component */
const RadialProgress = ({ pct, size = 140, strokeWidth = 10 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="tradial" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke="var(--color-dark-green-light)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke={pct >= 75 ? '#91cc47' : pct >= 50 ? '#d1dc24' : '#003a0f'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <span className="tradial__value">{pct}%</span>
    </div>
  );
};

/* Format helpers */
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
const fmtRelative = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};
const eventLabel = (name) => name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
const eventIcon = (name) => {
  if (name.includes('launch') || name.includes('start')) return <Play size={12} />;
  if (name.includes('complete')) return <Award size={12} />;
  if (name.includes('heartbeat') || name.includes('time')) return <Clock size={12} />;
  return <Activity size={12} />;
};

const TraineeAnalytics = () => {
  const { id: traineeId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [sortBy, setSortBy] = useState('progress');
  const [previewModuleRef, setPreviewModuleRef] = useState(null);

  useEffect(() => {
    const fetchTraineeAnalytics = async () => {
      try {
        setLoading(true);
        const response = await analyticsAPI.getTraineeAnalytics(traineeId);
        if (response.data.success) {
          const result = response.data.data;
          setAnalytics(result);
          const guid = result?.trainee?.userUuid;
          if (guid && /^\d+$/.test(String(traineeId))) {
            const dashless = guid.replace(/-/g, '');
            navigate(`/analytics/trainee/${dashless}`, { replace: true });
          }
        } else {
          setError('Failed to fetch trainee analytics');
        }
      } catch (err) {
        console.error('Error fetching trainee analytics:', err);
        setError(err.response?.data?.error || 'Failed to load trainee analytics');
        showNotification(err.response?.data?.error || 'Failed to load trainee analytics', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchTraineeAnalytics();
  }, [traineeId, navigate, showNotification]);

  if (loading) {
    return (
      <div className="analytics">
        <div className="page-header">
          <h1>Loading trainee analytics...</h1>
        </div>
        <div className="tradial-skeleton">
          <div className="skeleton-pulse" style={{ width: 140, height: 140, borderRadius: '50%' }} />
          <div className="skeleton-pulse" style={{ width: 200, height: 24 }} />
          <div className="skeleton-pulse" style={{ width: 160, height: 16 }} />
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="analytics">
        <div className="page-header">
          <div className="page-header-left">
            <button className="page-back" onClick={() => navigate('/analytics')}>
              <ArrowLeft size={12} /> Back to Analytics
            </button>
            <h1>Error loading trainee analytics</h1>
            <p>{error || 'Analytics data not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { trainee, overallProgress, totalModules, completedModules, totalTimeSpentHours,
          totalContentEnrolled, completedContent, moduleProgress, recentEvents, stats } = analytics;

  const sortedModules = [...moduleProgress].sort((a, b) => {
    if (sortBy === 'progress') return (b.progress || 0) - (a.progress || 0);
    if (sortBy === 'time') return (b.timeSpentSeconds || 0) - (a.timeSpentSeconds || 0);
    if (sortBy === 'recent') return new Date(b.lastAccessed || 0) - new Date(a.lastAccessed || 0);
    if (sortBy === 'name') return (a.title || '').localeCompare(b.title || '');
    return 0;
  });

  const toggleModule = (modId) => {
    setExpandedModules(prev => ({ ...prev, [modId]: !prev[modId] }));
  };

  const completionRate = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  return (
    <div className="analytics trainee-analytics">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <button className="page-back" onClick={() => navigate('/analytics')}>
            <ArrowLeft size={12} /> Back to Analytics
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="ta-hero">
        <div className="ta-hero__profile">
          <div className="ta-hero__avatar-wrap">
            <RadialProgress pct={overallProgress} size={150} strokeWidth={12} />
            <div className="ta-hero__avatar">
              {trainee.avatar ? (
                <img src={trainee.avatar} alt={trainee.name} />
              ) : (
                <User size={42} />
              )}
            </div>
          </div>
          <div className="ta-hero__info">
            <h1>{trainee.name}</h1>
            <div className="ta-hero__meta">
              <span className="badge badge-dark">{trainee.department}</span>
              <span className="badge badge-gray">{trainee.role}</span>
              {trainee.status !== 'Active' && (
                <span className="badge badge-gray" style={{ background: '#fee2e2', color: '#dc2626' }}>{trainee.status}</span>
              )}
            </div>
            <p className="ta-hero__email">{trainee.email}</p>
            <div className="ta-hero__progress-detail">
              <div className="ta-hero__progress-bar">
                <div className="progress-bar" style={{ height: 8, flex: 1 }}>
                  <div className="progress-bar-fill" style={{ width: `${overallProgress}%` }} />
                </div>
                <span className="ta-hero__pct">{overallProgress}%</span>
              </div>
              <div className="ta-hero__progress-stats">
                <span><strong>{totalModules}</strong> enrolled</span>
                <span className="ta-hero__dot" />
                <span><strong>{completedModules}</strong> completed</span>
                <span className="ta-hero__dot" />
                <span><strong>{totalTimeSpentHours}h</strong> spent</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="ta-stats">
        {[
          { icon: BookOpen, label: 'Enrolled Modules', value: totalModules, sub: `${completedModules} completed`, color: 'blue' },
          { icon: Award, label: 'Overall Progress', value: `${overallProgress}%`, sub: `${completionRate}% modules done`, color: 'green' },
          { icon: Clock, label: 'Time Invested', value: `${totalTimeSpentHours}h`, sub: `${(stats?.totalTimeSpentSeconds || 0).toLocaleString()}s total`, color: 'dark' },
          { icon: Layers, label: 'Content Progress', value: `${completedContent}/${totalContentEnrolled}`, sub: `${stats?.contentProgress || 0}% completed`, color: 'yellow' },
        ].map(({ icon: Icon, label, value, sub, color }) => (
          <div key={label} className={`ta-stat-card ta-stat-card--${color}`}>
            <div className="ta-stat-card__icon">
              <Icon size={22} />
            </div>
            <div className="ta-stat-card__body">
              <span className="ta-stat-card__value">{value}</span>
              <span className="ta-stat-card__label">{label}</span>
              <span className="ta-stat-card__sub">{sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Module Progress Cards */}
      <div className="ta-modules">
        <div className="ta-modules__header">
          <div>
            <h3><BookOpen size={18} /> Module Progress</h3>
            <p className="text-sm text-muted">Click a module to expand content details</p>
          </div>
          <div className="ta-modules__sort">
            <span className="text-sm text-muted">Sort:</span>
            {['progress', 'time', 'recent', 'name'].map((key) => (
              <button
                key={key}
                className={`ta-sort-btn ${sortBy === key ? 'active' : ''}`}
                onClick={() => setSortBy(key)}
              >
                {key === 'progress' ? 'Progress' : key === 'time' ? 'Time' : key === 'recent' ? 'Recent' : 'Name'}
              </button>
            ))}
          </div>
        </div>

        {sortedModules.length === 0 ? (
          <div className="ta-empty">
            <BookOpen size={40} />
            <p>No module enrollments found</p>
            <span>This trainee hasn't been enrolled in any modules yet.</span>
          </div>
        ) : (
          <div className="ta-modules__list">
            {sortedModules.map((mod) => {
              const isExpanded = !!expandedModules[mod.moduleId];
              const progressCls = mod.completed ? 'done' : mod.progress > 0 ? 'active' : 'idle';

              return (
                <div key={mod.moduleId} className={`ta-module-card ta-module-card--${progressCls}`}>
                  <div className="ta-module-card__main" onClick={() => toggleModule(mod.moduleId)}>
                    <div className="ta-module-card__indicator">
                      {mod.completed ? (
                        <CheckCircle size={28} className="ta-module-card__check" />
                      ) : mod.progress > 0 ? (
                        <svg width="28" height="28" viewBox="0 0 28 28">
                          <circle cx="14" cy="14" r="12" fill="none" stroke="var(--color-dark-green-light)" strokeWidth="3" />
                          <circle cx="14" cy="14" r="12" fill="none" stroke="var(--color-dark-green)" strokeWidth="3"
                            strokeDasharray={`${2 * Math.PI * 12}`}
                            strokeDashoffset={`${2 * Math.PI * 12 * (1 - mod.progress / 100)}`}
                            strokeLinecap="round" transform="rotate(-90 14 14)" />
                        </svg>
                      ) : (
                        <Circle size={28} className="ta-module-card__circle" />
                      )}
                    </div>
                    <div className="ta-module-card__body">
                      <div className="ta-module-card__top">
                        <span className="ta-module-card__title">{mod.title}</span>
                        <span className={`badge ${mod.completed ? 'badge-green' : mod.progress > 0 ? 'badge-yellow' : 'badge-gray'}`}>
                          {mod.completed ? 'Completed' : mod.progress > 0 ? 'In Progress' : 'Not Started'}
                        </span>
                      </div>
                      <div className="ta-module-card__meta">
                        <span className="badge badge-dark">{mod.category}</span>
                        <span>{mod.duration} min</span>
                        <span className="ta-module-card__dot" />
                        <span>{mod.timeSpentHours}h spent</span>
                        {mod.lastAccessed && (
                          <>
                            <span className="ta-module-card__dot" />
                            <span>Last: {fmtRelative(mod.lastAccessed)}</span>
                          </>
                        )}
                      </div>
                      <div className="ta-module-card__progress-row">
                        <div className="progress-bar" style={{ height: 6, flex: 1, borderRadius: 3 }}>
                          <div
                            className={`progress-bar-fill ${mod.completed ? 'progress-bar-fill--done' : ''}`}
                            style={{ width: `${mod.progress}%` }}
                          />
                        </div>
                        <span className={`ta-module-card__pct ${
                          mod.progress >= 75 ? 'analytics__table-pct--high' :
                          mod.progress >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                        }`}>
                          {mod.progress}%
                        </span>
                      </div>
                    </div>
                    <div className="ta-module-card__chevron">
                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="ta-module-card__detail">
                      <div className="ta-module-card__detail-grid">
                        <div className="ta-module-card__detail-stat">
                          <span className="ta-module-card__detail-value">{mod.timeSpentHours}h</span>
                          <span className="ta-module-card__detail-label">Time Spent</span>
                        </div>
                        <div className="ta-module-card__detail-stat">
                          <span className="ta-module-card__detail-value">{mod.progress}%</span>
                          <span className="ta-module-card__detail-label">Progress</span>
                        </div>
                        <div className="ta-module-card__detail-stat">
                          <span className="ta-module-card__detail-value">{mod.completed ? 'Yes' : 'No'}</span>
                          <span className="ta-module-card__detail-label">Completed</span>
                        </div>
                        {mod.lastAccessed && (
                          <div className="ta-module-card__detail-stat">
                            <span className="ta-module-card__detail-value">{fmtDate(mod.lastAccessed)}</span>
                            <span className="ta-module-card__detail-label">Last Accessed</span>
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-outline"
                        style={{ marginTop: 'var(--space-3)', fontSize: '0.8rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewModuleRef(mod.moduleId);
                        }}
                      >
                        <BarChart2 size={14} /> View Detailed Breakdown
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Content Overview */}
      <div className="ta-content-overview">
        <div className="card">
          <div className="card-header">
            <h3><FileText size={16} style={{ marginRight: 6 }} /> Content Completion Overview</h3>
          </div>
          <div className="card-body">
            <div className="ta-content-grid">
              <div className="ta-content-stat">
                <span className="ta-content-stat__value">{totalContentEnrolled}</span>
                <span className="ta-content-stat__label">Total Content</span>
              </div>
              <div className="ta-content-stat ta-content-stat--done">
                <span className="ta-content-stat__value">{completedContent}</span>
                <span className="ta-content-stat__label">Completed</span>
              </div>
              <div className="ta-content-stat ta-content-stat--remaining">
                <span className="ta-content-stat__value">{totalContentEnrolled - completedContent}</span>
                <span className="ta-content-stat__label">Remaining</span>
              </div>
            </div>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <div className="ta-content-bar">
                <div className="ta-content-bar__done" style={{ width: `${stats?.contentProgress || 0}%` }} />
                <div className="ta-content-bar__rest" style={{ width: `${100 - (stats?.contentProgress || 0)}%` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <span>{stats?.contentProgress || 0}% complete</span>
                <span>{totalContentEnrolled - completedContent} remaining</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="ta-activity">
        <div className="card">
          <div className="card-header">
            <h3><Activity size={16} style={{ marginRight: 6 }} /> Recent Activity</h3>
            <span className="badge badge-dark">Last {Math.min(recentEvents.length, 15)} of {recentEvents.length}</span>
          </div>
          <div className="card-body">
            {recentEvents.length === 0 ? (
              <div className="ta-empty">
                <Activity size={32} />
                <p>No activity recorded yet</p>
                <span>Events are logged when the trainee uses VR training modules.</span>
              </div>
            ) : (
              <div className="ta-timeline">
                {recentEvents.slice(0, 15).map((event, idx) => (
                  <div key={event.id} className={`ta-timeline__item ${idx === 0 ? 'ta-timeline__item--latest' : ''}`}>
                    <div className="ta-timeline__dot">
                      {eventIcon(event.eventName)}
                    </div>
                    <div className="ta-timeline__content">
                      <div className="ta-timeline__top">
                        <span className="ta-timeline__event">{eventLabel(event.eventName)}</span>
                        <span className="ta-timeline__time">{fmtRelative(event.eventTime)}</span>
                      </div>
                      <div className="ta-timeline__meta">
                        {event.sceneName && <span className="ta-timeline__scene">{event.sceneName}</span>}
                        {event.moduleId && <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>Module</span>}
                        {event.contentId && <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>Content</span>}
                        <span className="ta-timeline__date">{fmtDate(event.eventTime)} · {fmtTime(event.eventTime)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats (condensed) */}
      <div className="ta-summary">
        <div className="card">
          <div className="card-header">
            <h3><BarChart2 size={16} style={{ marginRight: 6 }} /> Summary</h3>
          </div>
          <div className="card-body">
            <div className="ta-summary-grid">
              {[
                { label: 'Total Time (seconds)', value: (stats?.totalTimeSpentSeconds || 0).toLocaleString() },
                { label: 'Total Time (hours)', value: `${stats?.totalTimeSpentHours || 0}h` },
                { label: 'Modules Enrolled', value: stats?.totalEnrolledModules || 0 },
                { label: 'Modules Completed', value: stats?.completedModules || 0 },
                { label: 'Content Enrolled', value: stats?.totalContentEnrolled || 0 },
                { label: 'Content Completed', value: stats?.completedContent || 0 },
                { label: 'Last Activity', value: stats?.lastActivity ? fmtRelative(stats.lastActivity) : 'Never' },
                { label: 'Events Recorded', value: recentEvents.length },
              ].map(({ label, value }) => (
                <div key={label} className="ta-summary-item">
                  <span className="ta-summary-item__value">{value}</span>
                  <span className="ta-summary-item__label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PersonalModuleSummaryModal
        isOpen={!!previewModuleRef}
        onClose={() => setPreviewModuleRef(null)}
        moduleRef={previewModuleRef}
      />
    </div>
  );
};

export default TraineeAnalytics;
